import { ContentChildren, Directive, forwardRef, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { BehaviorSubject, debounceTime, map, Observable, skip, Subject, takeUntil } from 'rxjs';
import {
  AbstractControl,
  FormControl,
  FormControlStatus,
  NG_VALIDATORS,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';

@Directive({
  selector: '[uiControlValidator]',
  standalone: true,
  providers: [
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => UiControlValidatorDirective),
    },
  ],
})
export class UiControlValidatorDirective<ValueType> implements OnInit, OnDestroy, Validator {
  @ContentChildren(UiControlValidatorDirective)
  @ViewChildren(UiControlValidatorDirective)
  private childValidityCheckers: QueryList<UiControlValidatorDirective<ValueType>> | null = null;
  private _isRequired$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _onUpdateValidity$: Subject<void> = new Subject<void>();
  private _errors$: BehaviorSubject<ValidationErrors | null> = new BehaviorSubject<ValidationErrors | null>(null);
  private _unsubscribe$: Subject<void> = new Subject<void>();
  protected control: FormControl<ValueType | null> = new FormControl<ValueType | null>(null);
  protected parentControl: AbstractControl | undefined;
  protected isRequired$: Observable<boolean> = this._isRequired$.asObservable();
  protected unsubscribe$: Observable<void> = this._unsubscribe$.asObservable();
  protected isInvalid$: Observable<boolean> = this.control.statusChanges.pipe(
    map((status: FormControlStatus) => status === 'INVALID'),
  );
  public error$: Observable<string | null> = this._errors$.pipe(
    map((errors: ValidationErrors | null) => {
      if (!errors) {
        return null;
      }

      return Object.keys(errors)[0];
    }),
  );

  ngOnInit(): void {
    this._onUpdateValidity$.pipe(takeUntil(this._unsubscribe$), debounceTime(0), skip(1)).subscribe(() => {
      this.control.markAsTouched();
      this._errors$.next(this.control.errors);

      if (this.childValidityCheckers) {
        this.childValidityCheckers.forEach((item: UiControlValidatorDirective<ValueType>) => item.updateValidity());
      }
    });
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
  }

  updateValidity(): void {
    this._onUpdateValidity$.next();
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (this.parentControl) {
      if (this.parentControl.pristine) {
        this.updateValidity();
      }

      this.parentControl.markAsPristine({ onlySelf: true });
    }

    if (!this.parentControl) {
      this.parentControl = control;
      this.control.setValidators(control.validator);
      this.setRequired(control.hasValidator(Validators.required) || control.hasValidator(Validators.requiredTrue));
    }

    return this.control.invalid ? this.control.errors : null;
  }

  protected setRequired(isRequired: boolean): void {
    this._isRequired$.next(isRequired);
  }
}
